import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Chip, Input } from '@heroui/react'
import { api } from '../lib/api'
import { fromMappingRows, moveMappingRow, normaliseGroup, toMappingRows, type MappingRow } from '../lib/directory'
import { explain } from '../lib/staffErrors'
import { roleGivable } from '../lib/people'
import type { Branch, DirectorySettings, OrgRole, Permission } from '../lib/types'
import { Icon } from '../ui/icons'
import { EmptyState, StatusChip } from '../ui/parts'
import { roleLabel } from '../lib/roleNames'
import { useStaffText } from '../ui/StaffText'
import { Mark, shortGroup } from './connectParts'
import { ConnectionDrawer, TestSignIn } from './DirectoryOwner'
import { ChoiceChips } from './peopleKit'
import { Confirm, useFlash, useSave } from './sectionKit'
import { EDIT, InnerPage, ListCard, ListRow, ListTable, Notice, PageHead, RowArrow, SettingBlock, SettingRow, SettingsCard, useOpenRecord, Values } from './settingsKit'
import { Pick as SetupPick } from './setupKit'

/**
 * Directory sign-in: one organisation's LDAP or Active Directory. The connection
 * is the installation owner's alone (English, DirectoryOwner.tsx); the group
 * mappings are the organisation administrator's, within what they could give by hand.
 */

/** Beyond this many branches the drawer offers the product's select rather than
 *  a radio row per branch, so a large network does not make a page of radios. */
const BRANCH_CHOICES = 8

/** A key no row on this screen has used before, this session. Module-level so a
 *  remount — switching sections and back — never mints one already handed to a
 *  row still on screen; see docs on sibling keys in the implementer contract. */
let freshRowKey = 0
const newMappingKey = () => `mapping-${++freshRowKey}`

export function Directory({
  organisation,
  canConnect,
  branches,
}: {
  organisation?: string
  /** Whether this caller has tenant.manage — the installation's owner, working
   *  inside an organisation, or the hosted service's own operator. Only they may
   *  change the connection or try a sign-in. */
  canConnect: boolean
  branches: Branch[]
}) {
  const { lang, say } = useStaffText()
  const { flash, toast } = useFlash()
  // The owner's branch list spans every organisation; everybody else's is
  // already theirs. Filtered here rather than passed in already filtered, on the
  // same reasoning People.tsx filters its own — the branches belong to whichever
  // organisation is chosen, not to whichever is doing the choosing.
  const branchesHere = organisation ? branches.filter((b) => b.tenantId === organisation) : branches

  const [roles, setRoles] = useState<OrgRole[]>([])

  // What this caller may hand out (GET /permissions), which decides the roles a
  // group here may give.
  const [mayGive, setMayGive] = useState<Permission[]>([])

  // The roles a group here may give: those the caller could give a person by
  // hand — the server's mayAssign, which handleSaveDirectoryMappings applies.
  // The locked role is among them for whoever holds every organisation
  // permission, an organisation's own administrator included; the owner may
  // give every one.
  const assignableRoles: OrgRole[] = roles.filter((r) => roleGivable(r, mayGive))

  const [saved, setSaved] = useState<DirectorySettings | null>(null)
  const [connLoaded, setConnLoaded] = useState(false)
  const [rows, setRows] = useState<MappingRow[]>([])
  const [rowsLoaded, setRowsLoaded] = useState(false)
  const [error, setError] = useState('')
  const [moving, setMoving] = useState(false)
  // A group's mapping, a new one and the connection each open on a page of their own:
  // #directory/<row>, #directory/new, #directory/edit.
  const rec = useOpenRecord(rows, (r) => r.key, rowsLoaded && connLoaded, canConnect ? [EDIT] : [])
  const freshKey = useMemo(newMappingKey, [rec.isNew])

  useEffect(() => {
    let cancelled = false
    api
      .directory(organisation)
      .then(({ directory }) => {
        if (cancelled) return
        setSaved(directory)
        setConnLoaded(true)
      })
      .catch((err) => {
        if (!cancelled) setError(explain(err, lang, 'common.unreachable'))
      })
    api
      .directoryMappings(organisation)
      .then(({ mappings }) => {
        if (cancelled) return
        setRows(toMappingRows(mappings, newMappingKey))
        setRowsLoaded(true)
      })
      .catch((err) => {
        if (!cancelled) setError(explain(err, lang, 'common.unreachable'))
      })
    api
      .roles(organisation)
      .then(({ roles: listed }) => {
        if (!cancelled) setRoles(listed)
      })
      .catch(() => undefined)
    api
      .permissionScreens(organisation)
      .then(({ mayGive: given }) => {
        if (!cancelled) setMayGive(given)
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
    // lang deliberately not a dependency: a language switch must not refetch, and
    // the refusal it would reword is one already on screen.
  }, [organisation])

  // A row naming a role beyond what this caller may give by hand — never the
  // owner's, who may give every role. Carried over on save and never edited here:
  // internal/api/directory.go's handleSaveDirectoryMappings refuses a save that
  // drops or changes one.
  const isLocked = (row: MappingRow) =>
    row.gives === 'role' && Boolean(row.roleId) && !assignableRoles.some((r) => r.id === row.roleId)

  const branchName = (id?: string) => branchesHere.find((b) => b.id === id)?.name ?? say('connect.branchGone')
  const roleName = (roleId?: string) => {
    const r = roles.find((x) => x.id === roleId)
    return r ? roleLabel(r.roleKey, r.name, lang) : roleId ? say('connect.roleGone') : ''
  }

  // In the order the administrator set, tried in that order: the first role
  // mapping a person's groups match gives the role, since roles have no rank of
  // their own any more (docs/superpowers/specs/2026-10-03-roles-and-people-
  // design.md section 4). Branch mappings are always combined regardless of
  // order, so only role rows get the up/down controls below.
  const roleRows = rows.filter((r) => r.gives === 'role')
  const branchRows = rows.filter((r) => r.gives === 'branch')
  // Role rows this caller may actually move something past — a locked row (the
  // owner's own) is skipped exactly like a branch row, never a target to land
  // on, so the boundary a button disables itself at is this list's, not
  // roleRows's.
  const movableRoleRows = roleRows.filter((r) => !isLocked(r))

  // Moves one role mapping and saves at once — there is no draft state on this
  // list to hold it in between, unlike a row's own fields, which wait for the
  // drawer's Save.
  const move = async (key: string, direction: 'up' | 'down') => {
    const next = moveMappingRow(rows, key, direction, isLocked)
    if (next === rows) return
    setMoving(true)
    setError('')
    try {
      const { mappings } = await api.saveDirectoryMappings(organisation, fromMappingRows(next))
      setRows(toMappingRows(mappings, newMappingKey))
    } catch (err) {
      setError(explain(err, lang, 'common.unreachable'))
    } finally {
      setMoving(false)
    }
  }

  const mapRow = (row: MappingRow, order?: number) => {
    const gives = row.gives === 'role' ? roleName(row.roleId) : branchName(row.branchId)
    const locked = isLocked(row)
    const at = row.gives === 'role' && !locked ? movableRoleRows.findIndex((r) => r.key === row.key) : -1
    const cells = (
      <>
        {order !== undefined && (
          <span role="cell">
            <span className="dir-order">{order}</span>
          </span>
        )}
        <span className="dir-group" role="cell">
          <b>{shortGroup(row.group)}</b>
          <span className="dir-mono">{row.group}</span>
        </span>
        <span role="cell">
          <Chip size="sm" variant="secondary">
            {locked && <Mark name="lock" size={12} />}
            {gives}
          </Chip>
        </span>
      </>
    )
    // A locked row is read-only whole: its group and what it gives together are its identity.
    if (locked) {
      return (
        <div key={row.key} className="set-trow" role="row" title={say('directory.lockedRow')}>
          {cells}
          <span />
        </div>
      )
    }
    return (
      <ListRow key={row.key} actions onOpen={() => rec.open(row)}>
        {cells}
        {row.gives === 'role' ? (
          <span className="dir-move" role="cell">
            <Button
              isIconOnly
              variant="ghost"
              size="sm"
              onPress={() => void move(row.key, 'up')}
              isDisabled={moving || at <= 0}
              aria-label={say('directory.moveUp')}
            >
              <Icon name="up" size={16} />
            </Button>
            <Button
              isIconOnly
              variant="ghost"
              size="sm"
              onPress={() => void move(row.key, 'down')}
              isDisabled={moving || at < 0 || at >= movableRoleRows.length - 1}
              aria-label={say('directory.moveDown')}
            >
              <Icon name="down" size={16} />
            </Button>
          </span>
        ) : (
          <RowArrow />
        )}
      </ListRow>
    )
  }

  const addGroup = () => rec.open()
  const editingConn = canConnect && rec.code === EDIT && connLoaded
  const editing = rec.isNew
    ? rowsLoaded && {
        // No position: fromMappingRows sends each row's array index on save.
        row: { key: freshKey, group: '', gives: 'role' as const, roleId: assignableRoles[0]?.id },
        isNew: true,
      }
    : rec.row && !isLocked(rec.row) && { row: rec.row, isNew: false }

  if (editingConn) {
    return (
      <>
        <ConnectionDrawer
          back={say('admin.section.directory')}
          organisation={organisation}
          saved={saved}
          onClose={rec.close}
          onSaved={(directory) => {
            setSaved(directory)
            rec.close()
            flash(say('connect.connectionSaved'))
          }}
        />
        {toast}
      </>
    )
  }
  if (editing) {
    return (
      <>
        <MappingSheet
          key={rec.code}
          organisation={organisation}
          row={editing.row}
          isNew={editing.isNew}
          rows={rows}
          branches={branchesHere}
          assignableRoles={assignableRoles}
          flash={flash}
          onClose={rec.close}
          onSaved={(next) => {
            setRows(next)
            rec.close()
          }}
        />
        {toast}
      </>
    )
  }

  return (
    <>
      <PageHead
        title={say('admin.section.directory')}
        line={say('settings.directory.line')}
        actions={
          <>
            {canConnect && connLoaded && (
              <Button variant={saved ? 'secondary' : 'primary'} onPress={() => rec.nav.open(EDIT)}>
                {saved ? say('connect.edit') : say('connect.setUp')}
              </Button>
            )}
            <Button variant={saved || !canConnect ? 'primary' : 'secondary'} isDisabled={!rowsLoaded} onPress={addGroup}>
              <Icon name="plus" size={16} /> {say('directory.addGroup')}
            </Button>
          </>
        }
      />
      {error && <Notice className="os-gap">{error}</Notice>}

      {connLoaded && (
        <SettingsCard
          title={say('directory.connection')}
          aside={
            <>
              {!canConnect && (
                <span className="dir-note">
                  <Mark name="lock" size={14} /> {say('connect.ownerOnly')}
                </span>
              )}
              <StatusChip tone={saved?.enabled ? 'ok' : undefined}>
                {!saved ? say('connect.dirNone') : saved.enabled ? say('connect.dirOn') : say('connect.dirOff')}
              </StatusChip>
            </>
          }
        >
          {saved && (
            <>
              {saved.bindPasswordUnreadable && (
                <SettingBlock>
                  <Notice tone="warning">{say('directory.passwordUnreadable')}</Notice>
                </SettingBlock>
              )}
              <SettingRow label={say('settings.directory.address')}>
                <span className="dir-mono dir-addr">{saved.url}</span>
              </SettingRow>
              <SettingRow label={say('directory.howChecked')}>
                <span>
                  {saved.bindDn ? (
                    <>
                      {say('directory.serviceAccount')} <span className="dir-mono">{shortGroup(saved.bindDn)}</span>
                      {!saved.bindPasswordSet && (
                        <>
                          , <span className="dir-bad">{say('connect.passwordMissing')}</span>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      {say('connect.ownAccount')} <span className="dir-mono">{saved.userTemplate}</span>
                    </>
                  )}
                </span>
              </SettingRow>
              <SettingRow label={say('settings.directory.search')}>
                <Values
                  items={[
                    { label: say('directory.searchBase'), value: <span className="dir-mono">{saved.baseDn || '—'}</span> },
                    { label: say('directory.searchFilter'), value: <span className="dir-mono">{saved.userFilter || '—'}</span> },
                  ]}
                />
              </SettingRow>
              <SettingRow label={say('settings.directory.signIn')}>
                <Values
                  items={[
                    { label: say('connect.firstSignIn'), value: saved.createAccounts ? say('connect.createsAccount') : say('connect.existingOnly') },
                    {
                      label: say('connect.directoryDown'),
                      value: saved.offlineGraceHours > 0 ? say('connect.graceHours', { n: saved.offlineGraceHours }) : say('connect.nobodySignsIn'),
                    },
                  ]}
                />
              </SettingRow>
            </>
          )}
        </SettingsCard>
      )}

      <ListCard title={say('connect.groups')} count={rowsLoaded ? say('settings.directory.order') : undefined}>
        {rowsLoaded &&
          (rows.length === 0 ? (
            <EmptyState icon={<Icon name="people" size={20} />} title={say('connect.noGroups')} />
          ) : (
            <>
              <ListTable
                label={say('directory.role')}
                cols="40px minmax(0, 1.4fr) minmax(140px, 1fr) 72px"
                head={['#', say('connect.directoryGroup'), say('directory.role'), '']}
              >
                {roleRows.map((r, i) => mapRow(r, i + 1))}
                {roleRows.length === 0 && <p className="dir-none">{say('connect.noRoleGroups')}</p>}
              </ListTable>
              <ListTable
                label={say('common.branch')}
                cols="minmax(0, 1.4fr) minmax(140px, 1fr) 16px"
                head={[say('connect.directoryGroup'), say('common.branch'), '']}
              >
                {branchRows.map((r) => mapRow(r))}
                {branchRows.length === 0 && <p className="dir-none">{say('connect.none')}</p>}
              </ListTable>
            </>
          ))}
      </ListCard>

      {canConnect && <TestSignIn organisation={organisation} branches={branchesHere} rows={rows} roles={roles} />}
      {toast}
    </>
  )
}

// ---------------------------------------------------------------------------
// One group mapping. The server keeps the mappings as one list, so Save sends
// the whole list with this row changed, added or taken out.

function MappingSheet({
  organisation,
  row,
  isNew,
  rows,
  branches,
  assignableRoles,
  flash,
  onClose,
  onSaved,
}: {
  organisation?: string
  row: MappingRow
  isNew: boolean
  rows: MappingRow[]
  branches: Branch[]
  assignableRoles: OrgRole[]
  flash: (text: string) => void
  onClose: () => void
  onSaved: (rows: MappingRow[]) => void
}) {
  const { lang, say } = useStaffText()
  const noop = useCallback(() => {}, [])
  const { busy, error, save } = useSave(noop, flash)
  const [group, setGroup] = useState(row.group)
  const [gives, setGives] = useState<'role' | 'branch'>(row.gives)
  const [roleId, setRoleId] = useState(row.roleId)
  const [branchId, setBranchId] = useState(row.branchId ?? '')

  const g = group.trim()
  const next: MappingRow =
    gives === 'role'
      ? { key: row.key, group: g, gives, roleId, position: row.position }
      : { key: row.key, group: g, gives, branchId, position: row.position }
  // The same group giving the same thing twice: the server refuses it
  // (org.ResolveGroups), and so does lib/directory's mappingProblems, in words
  // that number the rows of the old form. Here there is one row in view, so it
  // is said about this one.
  const clash =
    g !== '' &&
    rows.some(
      (r) =>
        r.key !== row.key &&
        r.gives === gives &&
        normaliseGroup(r.group) === normaliseGroup(g) &&
        (gives === 'role' ? r.roleId === roleId : r.branchId === branchId),
    )
  const valid = g !== '' && !clash && (gives === 'role' ? Boolean(roleId) : branchId !== '')

  const send = async (list: MappingRow[], said: string) => {
    let kept: MappingRow[] = list
    const ok = await save(async () => {
      const { mappings } = await api.saveDirectoryMappings(organisation, fromMappingRows(list))
      kept = toMappingRows(mappings, newMappingKey)
    }, said)
    if (ok) onSaved(kept)
  }

  const submit = () =>
    void send(
      isNew ? [...rows, next] : rows.map((r) => (r.key === row.key ? next : r)),
      say('connect.groupSaved'),
    )

  const branchOptions = branches.map((b) => ({
    value: b.id,
    label: b.name,
    note: b.active ? undefined : say('common.retired'),
  }))

  return (
    <InnerPage
      back={say('admin.section.directory')}
      title={isNew ? say('connect.newGroup') : shortGroup(row.group)}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={isNew ? say('directory.addGroup') : say('common.save')}
      valid={valid}
      busy={busy}
      error={error || undefined}
      extra={
        isNew ? undefined : (
          <Confirm
            ask={say('connect.removeGroup')}
            question={say('connect.removeAsk')}
            yes={say('common.remove')}
            keep={say('connect.keep')}
            busy={busy}
            onYes={() => void send(rows.filter((r) => r.key !== row.key), say('connect.groupRemoved'))}
          />
        )
      }
    >
      <label className="field">
        <span>{say('connect.directoryGroup')}</span>
        <Input
          fullWidth
          className="dir-mono-input"
          value={group}
          onChange={(e) => setGroup(e.target.value)}
          placeholder="CN=Tellers,OU=Groups,DC=bank,DC=az"
          aria-invalid={clash}
        />
        {/^\s*cn=[^,]+/i.test(g) && (
          <small>
            {say('connect.shownAs')} <strong>{shortGroup(g)}</strong>
          </small>
        )}
      </label>
      {clash && <p className="field-error">{say('connect.groupClash')}</p>}

      <div className="field">
        <span>{say('directory.gives')}</span>
        <ChoiceChips
          name="gives"
          label={say('directory.gives')}
          value={gives}
          onChange={(v) => setGives(v as 'role' | 'branch')}
          options={[
            { value: 'role', label: say('directory.givesRole') },
            { value: 'branch', label: say('directory.givesBranch') },
          ]}
        />
      </div>

      {gives === 'role' ? (
        <div className="field">
          <span>{say('directory.role')}</span>
          <ChoiceChips
            name="role"
            label={say('directory.role')}
            value={roleId ?? ''}
            onChange={setRoleId}
            options={assignableRoles.map((r) => ({ value: r.id, label: roleLabel(r.roleKey, r.name, lang) }))}
          />
        </div>
      ) : (
        <div className="field">
          <span>{say('common.branch')}</span>
          {branches.length > BRANCH_CHOICES ? (
            <SetupPick label={say('common.branch')} value={branchId} onChange={setBranchId} placeholder={say('directory.chooseBranch')} options={branchOptions} />
          ) : (
            <ChoiceChips name="branch" label={say('common.branch')} value={branchId} onChange={setBranchId} options={branchOptions} />
          )}
        </div>
      )}
    </InnerPage>
  )
}

